import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, ShieldCheck, ArrowLeft, LogOut, Loader2, ChevronRight,
  Building2, UserCog, Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const VERTICAL_LABELS = {
  law_enforcement: "Police", fire_service: "Fire", ems: "EMS",
  dispatch_psap: "Dispatch", corrections: "Corrections", school_safety: "School Safety",
  military: "Military", emergency_medicine: "Emergency Medicine",
};

const VERTICAL_TONE = {
  law_enforcement:   "border-blue-500/40 bg-blue-500/5",
  fire_service:      "border-red-500/40 bg-red-500/5",
  ems:               "border-emerald-500/40 bg-emerald-500/5",
  dispatch_psap:     "border-purple-500/40 bg-purple-500/5",
  corrections:       "border-slate-500/40 bg-slate-500/5",
  school_safety:     "border-amber-500/40 bg-amber-500/5",
  military:          "border-yellow-600/40 bg-yellow-600/5",
  emergency_medicine:"border-teal-500/40 bg-teal-500/5",
};

export default function PlatformPersonas() {
  const { user, checkAuth, logout } = useAuth();
  const nav = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/persona/available");
      setPersonas(r.data.personas);
    } catch {
      toast.error("Could not load personas.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const switchTo = async (p) => {
    if (p.type === "platform_admin") { nav("/platform"); return; }
    setSwitching(p.user_id);
    try {
      await api.post("/demo/impersonate", { user_id: p.user_id });
      await checkAuth?.();
      if (p.type === "demo_member") {
        try { localStorage.setItem("haruki_demo_run_first_scenario", "1"); }
        catch { /* localStorage unavailable */ }
      }
      toast.success(`Viewing as ${p.label}`);
      nav(p.route || "/agency");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch persona");
    } finally { setSwitching(null); }
  };

  // Group personas by demo_id so admin + members of one demo are visually together
  const platformSelf = personas.find(p => p.type === "platform_admin");
  const demoGroups = {};
  personas.forEach(p => {
    if (p.type === "platform_admin") return;
    if (!demoGroups[p.demo_id]) {
      demoGroups[p.demo_id] = { admin: null, members: [], vertical: p.vertical,
                                 label: p.type === "demo_admin"
                                          ? p.label.replace(" — Agency Admin", "")
                                          : p.agency_display_name };
    }
    if (p.type === "demo_admin") demoGroups[p.demo_id].admin = p;
    else demoGroups[p.demo_id].members.push(p);
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-teal))" }}>
              <UserCog className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight">Haruki Health</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>
                Admin · Persona picker
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/platform" className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="personas-back-platform">
              <ArrowLeft className="mr-1 inline h-3 w-3" />Platform overview
            </Link>
            <span className="hidden text-xs text-muted-foreground sm:inline">{user?.email}</span>
            <Button data-testid="personas-logout-button" variant="ghost" size="sm"
                    onClick={async () => { await logout(); nav("/"); }}>
              <LogOut className="mr-2 h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 lg:px-10">
        <div>
          <h1 className="font-heading text-3xl font-bold">Which persona today?</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Pick any view — you can jump between the platform admin console and any
            active demo workspace. One-tap &ldquo;Return to admin view&rdquo; always brings you back.
          </p>
        </div>

        {/* Platform-admin self card */}
        {platformSelf && (
          <div className="mt-8">
            <div className="mb-2 text-[11px] uppercase tracking-widest text-muted-foreground">Real management</div>
            <button data-testid="personas-platform-card"
                    onClick={() => switchTo(platformSelf)}
                    className="flex w-full items-center justify-between rounded-lg border border-border bg-card p-5 text-left transition hover:border-emerald-500/60 hover:bg-emerald-500/5">
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-emerald-500/15 p-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <div className="font-heading text-lg font-bold">Platform admin console</div>
                  <div className="text-xs text-muted-foreground">Real data · agency management · audit log</div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        )}

        {/* Demo personas */}
        <div className="mt-10">
          <div className="mb-2 text-[11px] uppercase tracking-widest text-muted-foreground">
            Demo workspaces {Object.keys(demoGroups).length > 0 && `· ${Object.keys(demoGroups).length}`}
          </div>
          {loading ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" /> Loading personas…
            </div>
          ) : Object.keys(demoGroups).length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center">
              <Sparkles className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
              <div className="font-heading text-lg">No active demos yet</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Create one on <Link to="/platform/demos" className="underline">Prospect Demos</Link> and it will appear here.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {Object.entries(demoGroups).map(([demoId, g]) => (
                <div key={demoId} data-testid={`personas-demo-${demoId}`}
                     className={`rounded-lg border p-4 ${VERTICAL_TONE[g.vertical] || "border-border"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-heading text-base font-bold">{g.label}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {VERTICAL_LABELS[g.vertical] || g.vertical}
                      </div>
                    </div>
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  </div>

                  {g.admin && (
                    <button data-testid={`personas-switch-admin-${demoId}`}
                            onClick={() => switchTo(g.admin)}
                            disabled={switching === g.admin.user_id}
                            className="mt-3 flex w-full items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-left text-sm hover:bg-muted/40 disabled:opacity-50">
                      <span className="flex items-center gap-2">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        View as agency admin
                      </span>
                      {switching === g.admin.user_id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                    </button>
                  )}

                  {g.members.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                        Or a sample employee
                      </div>
                      {g.members.map(m => (
                        <button key={m.user_id}
                                data-testid={`personas-switch-member-${m.user_id}`}
                                onClick={() => switchTo(m)}
                                disabled={switching === m.user_id}
                                className="flex w-full items-center justify-between rounded-md border border-transparent px-2 py-1.5 text-left text-xs hover:border-border hover:bg-muted/30 disabled:opacity-50">
                          <span className="flex items-center gap-1.5">
                            <Users className="h-3 w-3 text-muted-foreground" />
                            {m.label.split(" · ").slice(0, 2).join(" · ")}
                          </span>
                          {switching === m.user_id && <Loader2 className="h-3 w-3 animate-spin" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
